import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Moon,
  Mail,
  ShieldCheck,
  Lock,
  Clock,
  Smartphone,
  RefreshCcw,
  Check,
  Star,
  Timer,
} from "lucide-react";
import "./otp.css";
import Swal from "sweetalert2";
import {
  resendOTPfunction,
  completeRegistrationHandshake,
} from "../../api/login_api"; // Import the resend function

const Otp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get OTP and phone number from previous state
  const {
    Otp: initialOtp,
    phoneNumber: phone,
    otpSent,
    isRegistration,
  } = location.state || {};

  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [timer, setTimer] = useState(20);
  const [currentOtp, setCurrentOtp] = useState(initialOtp); // Track current valid OTP
  const [isResending, setIsResending] = useState(false);
  const [otpWasSent, setOtpWasSent] = useState(otpSent !== false); // Track if OTP was actually sent
  const inputRefs = useRef([]);

  // Get phone number from previous state or use default
  const phoneNumber = phone || "+91 98765 43210";

  // Check if we arrived here without a valid OTP
  useEffect(() => {
    if (!initialOtp || otpSent === false) {
      Swal.fire({
        title: "OTP Not Sent!",
        text: "There was an error sending the OTP. Please try again.",
        icon: "error",
        confirmButtonText: "Go Back",
      }).then(() => {
        navigate("/"); // Navigate back to login page
      });
    }
  }, [initialOtp, otpSent, navigate]);

  // Timer countdown effect
  useEffect(() => {
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  // Handle Input Change
  const handleChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return; // Only digits allowed
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move focus to next input (guarded)
    if (value && index < otp.length - 1) {
      const next = inputRefs.current[index + 1];
      if (next) next.focus();
    }
  };

  // Handle Backspace
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  // Format time for display (e.g., 00:20)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins < 10 ? "0" + mins : mins}:${secs < 10 ? "0" + secs : secs}`;
  };

  const handleVerify = async () => {
    const otpCode = otp.join("");

    // Check if OTP was actually sent successfully
    if (!otpWasSent || !currentOtp) {
      Swal.fire({
        title: "Error!",
        text: "OTP was not sent successfully. Please request a new code.",
        icon: "error",
      });
      return;
    }

    if (otpCode.length !== 6) {
      Swal.fire({
        title: "Incomplete OTP!",
        text: "Please enter all 6 digits",
        icon: "warning",
      });
      return;
    }

    console.log("Verifying OTP:", otpCode);
    console.log("Current Valid OTP:", currentOtp);

    if (otpCode === currentOtp) {
      // --- NEW LOGIC START ---
      if (isRegistration) {
        // If this is the registration flow, send the GET request to backend
        const handshake = await completeRegistrationHandshake(phoneNumber);
        if (!handshake.success) {
          Swal.fire({
            title: "Verification Failed",
            text: "Could not complete account setup. Please contact support.",
            icon: "error",
          });
          return;
        }
      }
      // --- NEW LOGIC END ---

      Swal.fire({
        title: "Success!",
        text: isRegistration
          ? "Account verified successfully!"
          : "Logged in successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      }).then(() => {
        navigate("/dashboard");
      });
    } else {
      Swal.fire({
        title: "Invalid OTP!",
        text: "Please enter the correct verification code",
        icon: "error",
      });
    }
  };

  // Resend OTP Logic
  const handleResend = async () => {
    if (timer > 0) return; // Don't resend if timer is still running

    setIsResending(true);

    try {
      // Extract phone number without country code if present
      const cleanPhone = phoneNumber.replace("+91", "").trim();

      // Call the resend API function
      const result = await resendOTPfunction(cleanPhone);

      if (result.success) {
        // Update the current OTP with the new one
        setCurrentOtp(result.otp);
        setOtpWasSent(true);

        // Reset form state
        setOtp(new Array(6).fill(""));
        setTimer(20);

        // Focus first input
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }

        // Show success message
        Swal.fire({
          title: "Code Resent!",
          text: `New verification code sent to ${phoneNumber}`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        // For demo purposes, log the new OTP (remove in production)
        console.log("New OTP sent:", result.otp);
      } else {
        // Handle API error
        setOtpWasSent(false);
        Swal.fire({
          title: "Error!",
          text: result.error || "Failed to resend code. Please try again.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      setOtpWasSent(false);
      Swal.fire({
        title: "Error!",
        text: "An unexpected error occurred. Please try again.",
        icon: "error",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="wc-otp-container">
      {/* Left Section - Info & Features */}
      <div className="wc-otp-left-section">
        <div className="wc-otp-secure-badge">
          <ShieldCheck size={16} className="wc-otp-shield-icon" /> Secure
          Verification
        </div>

        <h1 className="wc-otp-heading">
          Protect Your <br />
          <span className="wc-otp-heading-highlight">Digital Identity</span>
        </h1>

        <p className="wc-otp-description">
          Two-factor authentication adds an extra layer of security to your
          account. Enter the verification code sent to your phone.
        </p>

        <div className="wc-otp-features-grid">
          <div className="wc-otp-feature-item">
            <div className="wc-otp-feature-icon icon-blue">
              <Lock size={20} />
            </div>
            <div className="wc-otp-feature-text">
              <h4>Bank-Level Security</h4>
              <p>256-bit encryption protects your data</p>
            </div>
          </div>

          <div className="wc-otp-feature-item">
            <div className="wc-otp-feature-icon icon-purple">
              <Clock size={20} />
            </div>
            <div className="wc-otp-feature-text">
              <h4>Time-Limited Codes</h4>
              <p>Codes expire after 90 seconds</p>
            </div>
          </div>

          <div className="wc-otp-feature-item">
            <div className="wc-otp-feature-icon icon-green">
              <Smartphone size={20} />
            </div>
            <div className="wc-otp-feature-text">
              <h4>SMS Verification</h4>
              <p>Sent directly to your device</p>
            </div>
          </div>

          <div className="wc-otp-feature-item">
            <div className="wc-otp-feature-icon icon-orange">
              <RefreshCcw size={20} />
            </div>
            <div className="wc-otp-feature-text">
              <h4>Easy Resend</h4>
              <p>Request a new code anytime</p>
            </div>
          </div>
        </div>

        <div className="wc-otp-footer-trust">
          <span className="wc-otp-check-icon">
            <Check size={16} strokeWidth={3} />
          </span>{" "}
          Trusted by 10M+ users
          <span className="wc-otp-rating">
            <Star size={14} fill="#f59e0b" strokeWidth={0} /> 4.9/5 Rating
          </span>
        </div>
      </div>

      {/* Right Section - OTP Card */}
      <div className="wc-otp-right-section">
        <div className="wc-otp-card">
          <div className="wc-otp-card-header">
            <button className="wc-otp-nav-btn" onClick={() => navigate("/")}>
              <ArrowLeft size={20} />
            </button>
            <button className="wc-otp-nav-btn">
              <Moon size={20} />
            </button>
          </div>

          <div className="wc-otp-icon-wrapper">
            <div className="wc-otp-main-icon">
              <Mail size={36} color="#ffffff" strokeWidth={2} />
            </div>
          </div>

          <h2 className="wc-otp-card-title">Check Your Phone</h2>
          <p className="wc-otp-card-subtitle">
            We've sent a verification code to <br />
            <strong>{phoneNumber}</strong>
          </p>

          <label className="wc-otp-label">Enter Verification Code</label>
          <div className="wc-otp-input-group">
            {otp.map((data, index) => (
              <input
                key={index}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                className="wc-otp-digit-input"
                name={`otp-${index}`}
                maxLength={1}
                value={data}
                ref={(el) => (inputRefs.current[index] = el)}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={(e) => e.target.select()}
                autoComplete="one-time-code"
              />
            ))}
          </div>

          <button className="wc-otp-verify-btn" onClick={handleVerify}>
            Verify Code →
          </button>

          <div className="wc-otp-timer-section">
            <Timer size={16} className="wc-otp-timer-icon" />
            <span>{formatTime(timer)}</span>
            <button
              className="wc-otp-resend-btn"
              onClick={handleResend}
              disabled={timer > 0 || isResending}
            >
              {isResending ? "Sending..." : "Resend Code"}
            </button>
          </div>

          <div className="wc-otp-support-link">
            <span>Having trouble? </span>
            <a href="/support">Contact Support</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Otp;
